import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  Check,
  X,
  Save,
  RefreshCw,
  Loader2,
  Info,
  ChevronDown,
  ChevronRight,
  Users,
  Package,
  Ticket,
  BarChart3,
  Settings,
  History,
  Building,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import api from '@/services/api';

interface Permission {
  id: string;
  code: string;
  name: string;
  description: string;
  module: string;
  action: string;
}

interface PermissionsByModule {
  [module: string]: Permission[];
}

const ROLES = [
  { id: 'SUPER_ADMIN', name: 'Super Admin', description: 'Full system access', color: 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' },
  { id: 'ADMIN', name: 'Admin', description: 'Administrative access', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  { id: 'MANAGER', name: 'Manager', description: 'Team management', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  { id: 'SUPERVISOR', name: 'Supervisor', description: 'Oversee operations', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' },
  { id: 'TECHNICIAN', name: 'Technician', description: 'Handle tickets', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  { id: 'STAFF', name: 'Staff', description: 'Basic access', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400' },
  { id: 'VIEWER', name: 'Viewer', description: 'Read-only', color: 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400' },
];

const MODULE_ICONS: Record<string, typeof Shield> = {
  assets: Package,
  users: Users,
  employees: Users,
  tickets: Ticket,
  reports: BarChart3,
  settings: Settings,
  audit: History,
  organization: Building,
};

export default function PermissionsPage() {
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState('ADMIN');
  const [rolePermissions, setRolePermissions] = useState<Set<string>>(new Set());
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set(['assets', 'users']));
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch all permissions
  const { data: permissionsData, isLoading: loadingPermissions } = useQuery({
    queryKey: ['permissions'],
    queryFn: async () => {
      const response = await api.get('/permissions');
      return response.data.data as PermissionsByModule;
    },
  });

  // Fetch permissions for selected role
  const { data: rolePermsData, isLoading: loadingRolePerms } = useQuery({
    queryKey: ['role-permissions', selectedRole],
    queryFn: async () => {
      const response = await api.get(`/permissions/roles/${selectedRole}`);
      return response.data.data as Permission[];
    },
  });

  // Initialize permissions
  const initMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/permissions/initialize');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      queryClient.invalidateQueries({ queryKey: ['role-permissions'] });
      alert('Permissions initialized successfully!');
    },
  });

  // Save role permissions
  const saveMutation = useMutation({
    mutationFn: async (permissions: string[]) => {
      const response = await api.put(`/permissions/roles/${selectedRole}`, { permissions });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-permissions', selectedRole] });
      setHasChanges(false);
      alert('Permissions saved successfully!');
    },
  });

  // Update local state when role permissions load
  useEffect(() => {
    if (rolePermsData) {
      setRolePermissions(new Set(rolePermsData.map(p => p.code)));
      setHasChanges(false);
    }
  }, [rolePermsData]);

  const toggleModule = (module: string) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(module)) {
      newExpanded.delete(module);
    } else {
      newExpanded.add(module);
    }
    setExpandedModules(newExpanded);
  };

  const togglePermission = (code: string) => {
    if (selectedRole === 'SUPER_ADMIN') return; // Can't modify super admin
    
    const newPerms = new Set(rolePermissions);
    if (newPerms.has(code)) {
      newPerms.delete(code);
    } else {
      newPerms.add(code);
    }
    setRolePermissions(newPerms);
    setHasChanges(true);
  };

  const toggleAllInModule = (_module: string, permissions: Permission[]) => {
    if (selectedRole === 'SUPER_ADMIN') return;
    
    const moduleCodes = permissions.map(p => p.code);
    const allSelected = moduleCodes.every(code => rolePermissions.has(code));
    
    const newPerms = new Set(rolePermissions);
    if (allSelected) {
      moduleCodes.forEach(code => newPerms.delete(code));
    } else {
      moduleCodes.forEach(code => newPerms.add(code));
    }
    setRolePermissions(newPerms);
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(Array.from(rolePermissions));
  };

  const handleReset = () => {
    if (rolePermsData) {
      setRolePermissions(new Set(rolePermsData.map(p => p.code)));
      setHasChanges(false);
    }
  };

  const isLoading = loadingPermissions || loadingRolePerms;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="w-8 h-8 text-primary" />
            Permissions Management
          </h1>
          <p className="text-muted-foreground">
            Configure role-based access control for your organization
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => initMutation.mutate()}
            disabled={initMutation.isPending}
          >
            {initMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Initialize Defaults
          </Button>
          {hasChanges && (
            <>
              <Button variant="outline" size="sm" onClick={handleReset}>
                <X className="w-4 h-4 mr-2" />
                Reset
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
        <div className="text-sm text-blue-800 dark:text-blue-200">
          <p className="font-medium">How permissions work:</p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>Each role has a set of permissions that determine what actions users can perform</li>
            <li>Super Admin always has all permissions and cannot be modified</li>
            <li>Changes apply to all users with the selected role in your organization</li>
          </ul>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Role Selector */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Select Role</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {ROLES.map(role => (
                <button
                  key={role.id}
                  onClick={() => setSelectedRole(role.id)}
                  className={cn(
                    "w-full text-left p-3 rounded-lg border-2 transition-all",
                    selectedRole === role.id
                      ? "border-primary bg-primary/5"
                      : "border-transparent hover:bg-muted"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className={cn("px-2 py-0.5 text-xs font-medium rounded", role.color)}>
                      {role.name}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{role.description}</p>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Permissions Grid */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">
                  Permissions for {ROLES.find(r => r.id === selectedRole)?.name}
                </CardTitle>
                {selectedRole === 'SUPER_ADMIN' && (
                  <span className="text-xs bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-1 rounded">
                    All permissions (locked)
                  </span>
                )}
              </div>
              <span className="text-sm text-muted-foreground">
                {rolePermissions.size} permissions enabled
              </span>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : !permissionsData || Object.keys(permissionsData).length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No permissions found</p>
                  <Button variant="link" onClick={() => initMutation.mutate()}>
                    Initialize default permissions
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(permissionsData).map(([module, permissions]) => {
                    const ModuleIcon = MODULE_ICONS[module] || Shield;
                    const isExpanded = expandedModules.has(module);
                    const modulePermCodes = permissions.map(p => p.code);
                    const enabledCount = modulePermCodes.filter(code => rolePermissions.has(code)).length;
                    const allEnabled = enabledCount === permissions.length;
                    const someEnabled = enabledCount > 0 && !allEnabled;

                    return (
                      <div key={module} className="border rounded-lg">
                        <button
                          onClick={() => toggleModule(module)}
                          className="w-full flex items-center justify-between p-4 hover:bg-muted/50"
                        >
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                            <ModuleIcon className="w-5 h-5 text-muted-foreground" />
                            <span className="font-medium capitalize">{module}</span>
                            <span className="text-sm text-muted-foreground">
                              ({enabledCount}/{permissions.length})
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAllInModule(module, permissions);
                            }}
                            disabled={selectedRole === 'SUPER_ADMIN'}
                            className={cn(
                              "w-6 h-6 rounded border-2 flex items-center justify-center transition-colors",
                              allEnabled
                                ? "bg-primary border-primary text-white"
                                : someEnabled
                                ? "bg-primary/30 border-primary"
                                : "border-gray-300 dark:border-gray-600",
                              selectedRole === 'SUPER_ADMIN' && "opacity-50 cursor-not-allowed"
                            )}
                          >
                            {allEnabled && <Check className="w-4 h-4" />}
                            {someEnabled && <div className="w-2 h-2 bg-primary rounded-sm" />}
                          </button>
                        </button>

                        {isExpanded && (
                          <div className="border-t p-4 space-y-2 bg-muted/20">
                            {permissions.map(permission => (
                              <label
                                key={permission.id}
                                className={cn(
                                  "flex items-center justify-between p-2 rounded hover:bg-muted/50",
                                  selectedRole === 'SUPER_ADMIN' && "opacity-50 cursor-not-allowed"
                                )}
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm">{permission.name}</span>
                                    <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                                      {permission.code}
                                    </code>
                                  </div>
                                  <p className="text-xs text-muted-foreground">{permission.description}</p>
                                </div>
                                <button
                                  onClick={() => togglePermission(permission.code)}
                                  disabled={selectedRole === 'SUPER_ADMIN'}
                                  className={cn(
                                    "w-6 h-6 rounded border-2 flex items-center justify-center transition-colors",
                                    rolePermissions.has(permission.code)
                                      ? "bg-primary border-primary text-white"
                                      : "border-gray-300 dark:border-gray-600",
                                    selectedRole === 'SUPER_ADMIN' && "cursor-not-allowed"
                                  )}
                                >
                                  {rolePermissions.has(permission.code) && <Check className="w-4 h-4" />}
                                </button>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
